<template>
  <div class="title">
    Todo List
    <Menu ref="menu" :model="menuItems" :popup="true" />
  </div>
  <div @click="toggleMenu" class="menu-button flex-1">
    <i class="pi pi-ellipsis-v"></i>
  </div>
  <ProgressBar :value="progress" class="progress-bar" />
  <div
    @mouseenter="showScrollbar"
    @mouseleave="hideScrollbar"
    id="todos-list-container"
    ref="todoList"
    class="container no-scrollbar custom-scrollbar"
  >
    <div v-for="(todo, i) in todos" :key="i">
      <div
        v-show="displayChecked || !todo.checked"
        class="todo-container p-pt-2 p-pb-2"
      >
        <div class="todo p-d-flex p-ai-center p-jc-start">
          <Checkbox
            v-model="todo.checked"
            :binary="true"
            @change="checkItem(todo)"
          />
          <div
            v-if="editing !== todo.id"
            @click="showFullText"
            @mouseout="wrapText"
            class="todo-text p-ml-2"
            :style="{
              textDecoration: todo.checked ? 'line-through' : 'initial'
            }"
          >
            {{ todo.text }}
          </div>
          <input
            v-else
            :id="todo.id"
            v-model="todo.editText"
            @keyup.enter="saveEdit(todo)"
            @keyup.escape="editItem(todo, true)"
            @blur="checkSaveEdit(todo)"
            class="p-inputtext p-component"
          />
        </div>

        <div>
          <i
            v-if="editing !== todo.id"
            @click="editItem(todo)"
            class="action-icon pi pi-pencil"
          ></i>
          <i v-else @click="saveEdit(todo)" class="action-icon pi pi-check"></i>
          <i
            @click="deleteItem(todo)"
            class="action-icon pi pi-trash p-ml-2"
          ></i>
        </div>
      </div>
    </div>
  </div>
  <div class="footer">
    <textarea
      v-show="addingItem"
      ref="itemTextarea"
      @keyup.enter.prevent="addItem"
      @keyup.escape="cancelAdding"
      @blur="checkCloseInput"
      :style="textareaHeight"
      class="p-inputtextarea p-inputtext p-inputtextarea-resizable"
      v-model="newItem"
      placeholder="Add Item"
      rows="2"
      cols="30"
    />

    <Button @click="addItem" id="add-button" :label="addButtonLabel" />
    <Button
      v-show="addingItem"
      @click="cancelAdding"
      icon="pi pi-times"
      class="cancel-button p-button-rounded p-button-danger"
    />
  </div>
</template>

<style scoped lang="scss">
.container {
  height: 100%;
  width: 100%;
  overflow-y: scroll;
}
.header {
  position: sticky;
  top: 0;
}

.todo-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  padding: 0 0.5rem;
  border-radius: 6px;
  &:hover {
    background: #1f5d7e;
    margin-right: 0.5rem;
    .todo {
      max-width: calc(100% - 40px);
    }
    .action-icon {
      display: inline-block;
    }
  }
}

.todo {
  max-width: 100%;
  flex: 1;
}

.todo-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  cursor: pointer;
  flex: 1;
  padding-right: 1rem;
  color: #fff;
}

.full-text {
  white-space: normal;
  cursor: default;
}

.action-icon {
  cursor: pointer;
  font-size: 1rem;
  display: none;
}

.pi-check {
  margin-left: 1rem;
}

.menu-button {
  max-width: 12px;
}

.footer {
  padding: 0 0.5rem;
  margin: 0.5rem 0;

  textarea {
    width: 100%;
    margin-bottom: 0.5rem;
    &:focus {
      border: none;
      box-shadow: none;
    }
  }
}

.cancel-button {
  margin-left: 1rem;
}

input.p-inputtext {
  margin-left: 0.5rem;
  flex: 1;
  &:enabled:focus {
    box-shadow: none;
  }
}
</style>

<script>
import { ref, computed, onMounted } from "vue";
import useTodos from "../composables/todos.js";
import ProgressBar from "primevue/progressbar";
export default {
  props: {
    projectId: {
      required: true,
      type: String
    },
    expandLabel: {
      required: true,
      type: String
    }
  },
  components: {
    ProgressBar
  },
  //emits: ["resize"],
  setup(props) {
    const addingItem = ref(false);
    const newItem = ref(null);
    const itemTextarea = ref(null);
    const textareaHeight = ref({ height: "4rem" });
    const editing = ref(null);

    const {
      fetchTodos: use_fetchTodos,
      saveTodo: use_saveTodo,
      checkTodo: use_checkTodo,
      editTodoText: use_editTodoText,
      resetTodoEdit: use_resetTodoEdit,
      deleteTodo: use_deleteTodo
    } = useTodos();

    const menu = ref(null);
    function toggleMenu(ev) {
      menu.value.toggle(ev);
    }

    const menuItems = ref(null);

    const todos = ref(null);

    onMounted(async () => {
      todos.value = await use_fetchTodos(props.projectId);
      menuItems.value = [
        /* {
          label: props.expandLabel,
          icon: "pi pi-fw pi-window-maximize",
          command: () => {
            context.emit("resize");
          }
        }, */
        {
          label: displayLabel,
          icon: "pi pi-fw pi-ban",
          command: () => {
            toggleDisplay();
          }
        }
      ];
    });

    const displayLabel = computed(() => {
      let label;
      if (displayChecked.value) {
        label = "Hide checked";
      } else {
        const checked = getChecked();
        label = `Show checked (${checked.length})`;
      }
      return label;
    });

    const progress = computed(() => {
      const checked = getChecked();
      return checked.length
        ? Math.round((checked.length / todos.value.length) * 100)
        : "0";
    });

    function getChecked() {
      return todos.value
        ? todos.value.filter(item => item.checked === true)
        : [];
    }

    function editItem(item, escape = false) {
      if (editing.value) {
        use_resetTodoEdit(editing.value);
        if (escape) {
          editing.value = null;
          return;
        }
      }
      editing.value = item.id;
      setTimeout(() => {
        const editInput = document.getElementById(item.id);
        editInput.focus();
      }, 10);
    }

    function checkSaveEdit(todo) {
      if (todo.text === todo.editText) {
        editing.value = null;
      } else {
        saveEdit(todo);
      }
    }

    async function saveEdit(item) {
      if (
        item.editText &&
        item.editText.trim() &&
        item.editText.trim() !== item.text
      ) {
        await use_editTodoText(props.projectId, item.id, item.editText);
        item.text = item.editText;
      } else {
        item.editText = item.text;
      }
      editing.value = null;
    }
    const todoList = ref(null);
    async function addItem() {
      if (!addingItem.value) {
        addingItem.value = true;
        scrollToBottom();
      } else {
        await saveItem();
        newItem.value = null;
        scrollToBottom();
      }
    }

    function scrollToBottom() {
      setTimeout(() => {
        itemTextarea.value.focus();
        todoList.value.scrollTop = todoList.value.scrollHeight;
      }, 10);
    }

    function cancelAdding() {
      addingItem.value = false;
      newItem.value = null;
    }

    function resizeTextarea(reset = false) {
      if (reset === true) {
        textareaHeight.value.height = "4rem";
      } else
        textareaHeight.value.height = `${itemTextarea.value.scrollHeight +
          2}px`;
    }

    function checkCloseInput(ev) {
      if (ev.relatedTarget && ev.relatedTarget.id === "add-button") {
        return;
      } else {
        cancelAdding();
      }
    }

    const addButtonLabel = computed(() => {
      return !addingItem.value ? "Add" : "Save";
    });

    async function saveItem() {
      if (newItem.value && newItem.value.trim()) {
        const saveItem = {
          text: newItem.value.trim(),
          checked: false
        };
        await use_saveTodo(saveItem, props.projectId);
      } else {
        console.log("invalid item");
      }
    }

    function checkItem(item) {
      use_checkTodo(props.projectId, item.id, item.checked);
    }

    async function deleteItem(item) {
      if (editing.value) {
        use_resetTodoEdit(editing.value);
        editing.value = null;
      }
      await use_deleteTodo(props.projectId, item.id);
    }

    function showFullText(ev) {
      ev.target.classList.add("full-text");
    }

    function wrapText(ev) {
      ev.target.classList.remove("full-text");
    }

    const displayChecked = ref(true);
    function toggleDisplay() {
      displayChecked.value = !displayChecked.value;
    }

    function showScrollbar() {
      const eventsListWidget = document.getElementById("todos-list-container");
      eventsListWidget.classList.remove("no-scrollbar");
    }

    function hideScrollbar() {
      const eventsListWidget = document.getElementById("todos-list-container");
      eventsListWidget.classList.add("no-scrollbar");
    }

    return {
      todos,
      todoList,
      menu,
      toggleMenu,
      menuItems,
      addItem,
      cancelAdding,
      addingItem,
      newItem,
      saveItem,
      checkItem,
      deleteItem,
      addButtonLabel,
      itemTextarea,
      resizeTextarea,
      checkCloseInput,
      textareaHeight,
      editItem,
      checkSaveEdit,
      saveEdit,
      editing,
      progress,
      showFullText,
      wrapText,
      displayChecked,
      toggleDisplay,
      showScrollbar,
      hideScrollbar
    };
  }
};
</script>
